import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { GlPreviewLine, GlPreviewResponse } from '../../models/gl-preview.model';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';

export interface GlPreviewModalData {
  title: string;
  /** When true, show Another transaction / Start over for posted GL sampling (GL impact). */
  allowResample?: boolean;
  getPreview: (
    excludeTypenos?: number[],
    sampleScope?: 'all' | 'current_year'
  ) => Observable<GlPreviewResponse>;
}

@Component({
  selector: 'app-gl-preview-modal',
  templateUrl: './gl-preview-modal.component.html',
  styleUrls: ['./gl-preview-modal.component.scss']
})
export class GlPreviewModalComponent implements OnInit {
  loading = true;
  error: string | null = null;
  /** Success with no GL lines by design (e.g. systype does not post to GL). */
  infoMessage: string | null = null;
  lines: GlPreviewLine[] = [];
  assumptions: string[] = [];
  debitTotal = 0;
  creditTotal = 0;
  balanced = false;
  /** typenos already shown; next request excludes these plus the last sample */
  excludedTypenos: number[] = [];
  lastSampleTypeno: number | null = null;
  sampleExhausted = false;
  /** GL impact: optional filter; default all-time avoids “no postings this year” when history is older. */
  limitToCurrentYear = false;

  @ViewChild('pdfExportRoot', { static: false }) pdfExportRoot: ElementRef<HTMLElement> | undefined;
  exportingPdf = false;

  constructor(
    public dialogRef: MatDialogRef<GlPreviewModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: GlPreviewModalData,
    private globalSearchService: GlobalSearchService,
    private printService: PrintService
  ) {}

  ngOnInit(): void {
    this.loadPreview();
  }

  loadPreview(): void {
    this.loading = true;
    this.error = null;
    this.infoMessage = null;
    this.lines = [];
    this.assumptions = [];
    this.debitTotal = 0;
    this.creditTotal = 0;
    this.balanced = false;
    this.sampleExhausted = false;

    this.data
      .getPreview(this.excludedTypenos, this.limitToCurrentYear ? 'current_year' : 'all')
      .subscribe({
      next: (res: GlPreviewResponse) => {
        this.loading = false;
        if (res.success && res.meta?.no_gl) {
          this.infoMessage = res.message || '';
          return;
        }
        if (res.success && res.lines?.length) {
          this.assumptions = Array.isArray(res.meta?.assumptions) ? res.meta!.assumptions! : [];
          this.lines = res.lines;
          this.debitTotal = this.lines
            .filter((l) => l.side === 'debit')
            .reduce((s, l) => s + l.amount, 0);
          this.creditTotal = this.lines
            .filter((l) => l.side === 'credit')
            .reduce((s, l) => s + l.amount, 0);
          this.balanced = Math.abs(this.debitTotal - this.creditTotal) < 0.02;
          this.lastSampleTypeno = res.meta?.sample_typeno ?? null;
          return;
        }
        if (res.success && !res.lines?.length && res.message) {
          this.infoMessage = res.message;
          return;
        }
        this.error = res.message || (res.success ? 'No GL lines returned.' : 'Failed to load preview.');
        if (res.meta?.sample_exhausted) {
          this.sampleExhausted = true;
        }
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.error?.message || err?.message || 'Failed to load preview.';
      }
    });
  }

  anotherTransaction(): void {
    if (this.lastSampleTypeno != null) {
      this.excludedTypenos = [...this.excludedTypenos, this.lastSampleTypeno];
    }
    this.lastSampleTypeno = null;
    this.loadPreview();
  }

  startOverSamples(): void {
    this.excludedTypenos = [];
    this.lastSampleTypeno = null;
    this.loadPreview();
  }

  onSampleScopeChange(checked: boolean): void {
    this.limitToCurrentYear = checked;
    this.excludedTypenos = [];
    this.lastSampleTypeno = null;
    this.loadPreview();
  }

  close(): void {
    this.dialogRef.close();
  }

  exportXlsx(): void {
    if (!this.lines.length) {
      return;
    }
    const rows = this.lines.map((line) => ({
      Acct: line.account,
      Name: line.account_name,
      Narrative: line.narrative,
      Debit: line.side === 'debit' ? line.amount : '',
      Credit: line.side === 'credit' ? line.amount : '',
    }));
    rows.push({
      Acct: '',
      Name: '',
      Narrative: 'Totals',
      Debit: this.debitTotal,
      Credit: this.creditTotal,
    });
    this.globalSearchService.exportJsonToExcel(rows, this.exportFileBase());
  }

  exportPdf(): void {
    if (!this.lines.length || !this.pdfExportRoot?.nativeElement) {
      return;
    }
    this.exportingPdf = true;
    const filename = `${this.exportFileBase()}.pdf`;
    const titleBlock = `<h2 style="font-size:15px;margin:0 0 10px;font-family:sans-serif">${this.escapeHtml(this.data.title)}</h2>`;
    const encoded = this.globalSearchService.base64encode(titleBlock + this.pdfExportRoot.nativeElement.innerHTML);
    this.printService.pdf({ content: encoded, filename }).subscribe({
      next: (result: any) => {
        this.globalSearchService.downloadPdf(result.content, filename);
        this.exportingPdf = false;
      },
      error: () => {
        this.exportingPdf = false;
      },
    });
  }

  private exportFileBase(): string {
    const raw = (this.data.title || 'GL_preview')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
    return (raw || 'GL_preview') + '_' + new Date().toISOString().slice(0, 10);
  }

  private escapeHtml(text: string): string {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
